import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api.ts';

/** The signed-in staff member, from GET /staff/me. */
export interface KitchenMe {
  display_name: string;
  permissions: string[];
  locations?: { id: string; slug: string; name: string; pos_enabled?: boolean }[];
}

const LOCATION_KEY = 'crave.kitchenLocation';

function readSaved(): string | null {
  try {
    return localStorage.getItem(LOCATION_KEY);
  } catch {
    return null;
  }
}

/**
 * Who is signed in and which location this device works for. A tablet stays at one location, so
 * the choice is remembered on the device; with one location there is nothing to choose.
 */
export function useKitchenStaff() {
  const me = useQuery({
    queryKey: ['kitchen-me'],
    queryFn: async () => (await api.get<KitchenMe>('/staff/me')).data,
    staleTime: 5 * 60_000,
  });
  const [saved, setSaved] = useState(readSaved);
  const locations = me.data?.locations ?? [];
  const location = locations.find((l) => l.slug === saved) ?? locations[0] ?? null;
  const choose = (slug: string) => {
    try {
      localStorage.setItem(LOCATION_KEY, slug);
    } catch {
      // Private mode: the choice lasts for this visit.
    }
    setSaved(slug);
  };
  return {
    me,
    locations,
    /** Before /staff/me answers, the first location as it has always been. */
    slug: location?.slug ?? 'mobile-1',
    locationName: location?.name ?? null,
    /** Walk-up orders are on at this location (older APIs leave it out: on). */
    posEnabled: location?.pos_enabled !== false,
    choose,
    can: (p: string) => Boolean(me.data?.permissions.includes(p)),
  };
}
